import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { LoginScreen } from './screens/LoginScreen';
import { ChangePasswordScreen } from './screens/ChangePasswordScreen';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { ActivateAccountScreen } from './screens/ActivateAccountScreen';
import { VirtualRoutezOnboardingScreen } from './screens/VirtualRoutezOnboardingScreen';
import { CompletionOverrideReviewScreen } from './screens/CompletionOverrideReviewScreen';
import { AdminHome } from './screens/AdminHome';
import { ViewerHome } from './screens/ViewerHome';

interface ResetParams {
  uid: string;
  token: string;
}

const Inner: React.FC = () => {
  const { access, needsPasswordChange, isAdmin } = useAuth();
  const [resetParams, setResetParams] = useState<ResetParams | null>(null);
  const [activationParams, setActivationParams] = useState<ResetParams | null>(null);
  const [vrzOnboardingToken, setVrzOnboardingToken] = useState<string | null>(null);
  const [overrideReviewToken, setOverrideReviewToken] = useState<string | null>(null);

  // Check for deep link on mount
  useEffect(() => {
    const path = window.location.pathname;

    // Handle /reset/:uid/:token/ pattern
    if (path.startsWith('/reset/')) {
      const parts = path.split('/').filter(Boolean); // ['reset', uid, token]
      if (parts.length >= 3) {
        const uid = parts[1];
        const token = parts[2];
        setResetParams({ uid, token });
      }
    } else if (path.startsWith('/activate-account/')) {
      const parts = path.split('/').filter(Boolean); // ['activate-account', uid, token]
      if (parts.length >= 3) {
        setActivationParams({ uid: parts[1], token: parts[2] });
      }
    } else if (path.startsWith('/virtual-routez-onboarding/')) {
      // ['virtual-routez-onboarding', token]. Whoever opens this link is
      // Virtual Routez staff with no AVCS account -- render regardless of
      // any AVCS session that happens to exist in this browser.
      const parts = path.split('/').filter(Boolean);
      if (parts.length >= 2) {
        setVrzOnboardingToken(parts[1]);
      }
    } else if (path.startsWith('/deals/') && path.includes('/override/')) {
      // ['deals', dealId, 'override', token] -- the link from a close-out
      // override approval email. Unlike the Virtual Routez link this one
      // sits behind login: the decision is written against a superuser.
      const parts = path.split('/').filter(Boolean);
      if (parts.length >= 4 && parts[2] === 'override') {
        setOverrideReviewToken(parts[3]);
      }
    }
  }, []);

  if (vrzOnboardingToken) {
    return <VirtualRoutezOnboardingScreen token={vrzOnboardingToken} />;
  }

  if (activationParams && !access) {
    return <ActivateAccountScreen uid={activationParams.uid} token={activationParams.token} />;
  }

  // Show reset screen if we have reset params and user is not logged in
  if (resetParams && !access) {
    return (
      <ResetPasswordScreen
        uid={resetParams.uid}
        token={resetParams.token}
        onSuccess={() => {
          setResetParams(null);
          window.history.pushState({}, '', '/');
        }}
        onRequestNewLink={() => {
          setResetParams(null);
          window.history.pushState({}, '', '/');
          // User will see login screen which has forgot password link
        }}
      />
    );
  }

  // Standard auth flow
  if (!access) return <LoginScreen />;
  if (needsPasswordChange) return <ChangePasswordScreen />;
  if (overrideReviewToken) {
    return (
      <CompletionOverrideReviewScreen
        token={overrideReviewToken}
        onDone={() => {
          setOverrideReviewToken(null);
          window.history.pushState({}, '', '/');
        }}
      />
    );
  }
  if (isAdmin) return <AdminHome />;
  return <ViewerHome />;
};

export const AppRoot: React.FC = () => (
  <AuthProvider>
    <Inner />
  </AuthProvider>
);
